import {useEffect, useRef, useState, useCallback, useMemo} from 'react';
import {useDispatch, useSelector, shallowEqual} from 'react-redux';
import {useLocation} from 'react-router-dom';
import {createSelector} from 'reselect';
import {labels, setThousandSeparator} from './utils';
import env from './env';
import {
	clearComments,
	updateCommentCountAfterClearingComments
} from '../actions/EngagementComments/engagementcommentactions';
import {
	getUnresolvedCommentsCount,
	getDocumentValidations
} from '../actions/ValidationControl/validationcontrolactions';
import {
	ENGAGEMENTMETADATA_URL,
	EngagementStatus,
	clientSideEventNames,
	documentsApiExpandableItems,
	entities,
	frameSubEvent,
	serviceURL,
	validationTypes,
	bodyTypesWithEmbeddedDocumentTypes,
	formBodyType
} from './uiconstants';
import Utility from '../util/utilityfunctions';
import {getTaskDocumentByDocumentId} from '../selectors/Ares/selectors';
import {setDocumentId} from '../actions/Document/documentactions';
import {setApprovalsFromDocument} from '../actions/Task/signoffsaction';
import {expandableGetFormById, useAresUrlParameters, useIsAresView, useIsDrawerMode} from './Ares/customHooks';
import {getDocumentByDocumentId} from '../actions/Ares/taskDocumentActions';
import {raiseJSValidationError} from '../actions/erroractions';
import {getActiveAndGuestFormUsers} from '../actions/FormUsers/formusersactions';
import {getFormValidations} from '../components/Main/Ares/Tasks/TaskView/Common/guidedWorkflow.utils';
import {useFrameService, frameCommunicationEvents, frameEvents} from '@ey/canvascoreservices/ReactiveFrames';
import {getEngagement, getEngagementMetaData} from '../actions/Engagement/engagementactions';
import {getConfigurations} from '../actions/Configuration/configurationactions';
import {getQuickLinks} from '../actions/Engagement/quicklinksactions';
import {getMateriality} from '../actions/Materiality/materialityactions';
import {getFormBodyDataBySectionId} from '../selectors/Forms/formBodySelectors';

/**
 * Writes a line in the debug console each time the components re-renders, adding information about which props change caused the render.
 * (meant for internal debugging only. DO NOT USE IN PRODUCTION).
 * @param {string} name Component name used when logging the message.
 * @param {object} props props used in the component.
 */
export function useWhyDidYouUpdate(name, props) {
	// Get a mutable ref object where we can store props ...
	// ... for comparison next time this hook runs.
	const previousProps = useRef();

	useEffect(() => {
		if (!env.isProd()) {
			if (previousProps.current) {
				// Get all keys from previous and current props
				const allKeys = Object.keys({...previousProps.current, ...props});
				// Use this object to keep track of changed props
				const changesObj = {};
				// Iterate through keys
				allKeys.forEach((key) => {
					// If previous is different from current
					if (previousProps.current[key] !== props[key]) {
						// Add to changesObj
						changesObj[key] = {
							from: previousProps.current[key],
							to: props[key]
						};
					}
				});

				// If changesObj not empty then output to console
				if (Object.keys(changesObj).length) {
					console.log('[why-did-you-update]', name, changesObj);
				}
			}

			// Finally update previousProps with current props for next hook call
			previousProps.current = props;
		}
	});
}

/**
 * Retrieves the user preferences from the redux store.
 * @returns {Object} The user preferences.
 */
export function useUserPreferences() {
	return useSelector((state) => state.userPreferences);
}

/**
 * Returns the number separators to be used when formatting numbers, according to the user settings.
 * @returns {Object} Returns the number separators to be used when formatting numbers.
 */
export function useNumberFormatSettings() {
	const userPreferences = useUserPreferences();
	return setThousandSeparator(userPreferences.numberFormat);
}

/**
 * Returns the current Engagement Id.
 * @returns {Number | String} The current Engagement ID.
 */
export function useEngagementId() {
	let engagementId = useSelector((state) => Number(state.engagementId));
	if (!engagementId) {
		engagementId = Utility.getEngagementIdFromUrl(window);
		if (engagementId && engagementId instanceof Array) {
			engagementId = engagementId[0];
		}
		engagementId = Number(engagementId);
	}
	return engagementId;
}

/**
 * Returns the blackListAnalyzerTypes.
 * @returns {Number | String} true or false.
 */
export function useBlackListAnalyzerTypes() {
	const engagementHelixProjects = useSelector((store) => store.engagementHelixProjects, shallowEqual);
	const configurations = useSelector((state) => state.configurations, shallowEqual);
	const helixConnection = engagementHelixProjects.find((x) => x.isPrimary);

	return (
		helixConnection &&
		configurations &&
		configurations?.features &&
		configurations?.features?.blackListAnalyzerTypes.includes(helixConnection?.helixAnalyzerType?.toLowerCase())
	);
}

/**
 * Returns the current Engagement.
 * @returns {Object} The current Engagement.
 */
export function useEngagement() {
	return useSelector((state) => state.engagement);
}

/**
 * Returns the current Engagement Metadata.
 * @param {Number} engagementId The Engagement ID.
 * @returns {Object} The current Engagement Metadata.
 */
export function useEngagementMetadata(engagementId) {
	const metadata = useSelector((state) => state.engagementMetaData);
	if (metadata.totalpageditems === 0) {
		return {datacenterid: 0};
	} else {
		return metadata.engagementmetadatadetails?.find((x) => x.engagementid === engagementId) ?? {datacenterid: 0};
	}
}

/**
 * Returns the current configurations
 * @returns {Object} the current Configurations
 */
export function useConfigurations() {
	return useSelector((state) => state.configurations);
}

/**
 * Returns the application state.
 * @returns {Boolean} The application state.
 */
export function useAppState() {
	return useSelector((state) => state.apponlinestate);
}

/**
 * Returns the current errors in the application.
 * @returns {Object | String} The current errors.
 */
export function useErrors() {
	return useSelector((state) => state.errors);
}

/**
 * Returns the document's User Presence.
 * @param {String} documentId The Document ID.
 * @returns {Object | String} The document's User Presence.
 */
export function useUserPresence(documentId) {
	return useSelector((state) => state.userPresence[documentId]);
}

/**
 * Returns the current Document Id.
 * @returns {String} The current Document ID.
 */
export function useDocumentId() {
	return Utility.getDocumentIdFromUrl(window);
}

/**
 * Returns the current document Engagement Id.
 * @returns {Number | String} The document Engagement Id.
 */
export function useDocumentEngagementId() {
	return useCanvasFormMetadata().documentEngagementId;
}

function getDocument(state) {
	const documentId = Utility.getDocumentIdFromUrl(window);
	return getTaskDocumentByDocumentId(state, documentId);
}

function getEngagementId(state) {
	return state.engagementId;
}

const getFormMetadata = createSelector([getDocument, getEngagementId], (document, engagementId) => {
	return {
		isShared: document?.isShared,
		isTargetEngagement: document?.engagementId !== Number(engagementId),
		documentEngagementId: Number(document?.engagementId)
	};
});

/**
 * Returns basic metadata about the current Canvas Form being displayed on the UI.
 * @returns {Object} Current Canvas Form metadata.
 */
export function useCanvasFormMetadata() {
	return useSelector(getFormMetadata);
}

export function useWindowEventListener(eventName, callback) {
	useEffect(() => {
		window.addEventListener(eventName, callback);
		return () => {
			window.removeEventListener(eventName, callback);
		};
	}, [callback]);
}

export function useClearAllComments(
	engagementId,
	documentId,
	documentTypeId,
	entityId,
	entityUid,
	callback,
	bodyTypeId = undefined
) {
	const formBodyData = useSelector((store) => store.formBodyData),
		comments = useSelector((store) => store.engagementComments),
		formData = useSelector((state) => getTaskDocumentByDocumentId(state, documentId)),
		entityDocuments = useSelector((store) => store.entityDocuments),
		allValidations = useSelector((store) => store.validations);
	const dispatch = useDispatch();
	const [clearAllComments, setClearAllComments] = useState(false),
		[_entityId, setEntityId] = useState(entityId),
		[_entityUid, setEntityUid] = useState(entityUid);

	const execute = async () => {
		const documentWithUnresolvedComments = [];
		let bodyTypeIds = [];
		if (entityId === entities.FormSection && entityUid) {
			let sectionBodies = formBodyData.filter((fbd) => fbd.sectionId === Number(entityUid));
			if (sectionBodies) {
				bodyTypeIds.push(...new Set(sectionBodies[0].bodies?.map((body) => body.bodyTypeId)));
				if (bodyTypeIds.length > 0) {
					for (const bodyTypeId of bodyTypeIds) {
						const documentsWithUnresolvedComments = getDocumentsWithUnresolvedComments(
							bodyTypeId,
							entityDocuments,
							allValidations
						);
						documentWithUnresolvedComments.push(...documentsWithUnresolvedComments);
					}
				}
			}
		} else if (bodyTypeId) {
			const unresolvedComments = getDocumentsWithUnresolvedComments(bodyTypeId, entityDocuments, allValidations);
			documentWithUnresolvedComments.push(...unresolvedComments);
		}
		await dispatch(clearComments(engagementId, documentId, _entityId, _entityUid.toString())).then(async () => {
			if (documentId) {
				await clearAllCommentsOnEmbeddedDocuments(documentWithUnresolvedComments).then((results) => {
					dispatch(
						getUnresolvedCommentsCount(
							engagementId,
							documentId,
							documentTypeId,
							undefined,
							formData.embeddedBodyTypeIds
						)
					)
						.then(() => {
							if (formBodyData && formBodyData.length > 0) {
								switch (_entityId) {
									case entities.FormHeader: {
										/*find sections for headerId*/
										let sections = formData.headers.filter((fl) => {
											return Number(fl.id) === Number(_entityUid);
										})[0].sections;
										if (sections && sections.length > 0) {
											if (sections) {
												let bodyIds = [];
												sections.map((s) => {
													let section = formBodyData.filter((fbd) => {
														return fbd.sectionId === s.id;
													});
													if (section && section.length > 0) {
														let sectionsBodies = section[0].bodies?.map((body) => body.id);
														bodyIds.push(...sectionsBodies);
													}
												});
												if (bodyIds.length > 0) {
													dispatch(updateCommentCountAfterClearingComments(bodyIds));
												}
											}
										}
										break;
									}
									case entities.FormSection: {
										let sectionBodies = formBodyData.filter((fbd) => fbd.sectionId === Number(_entityUid));
										if (sectionBodies) {
											let bodyIds = [];
											bodyIds.push(sectionBodies[0].bodies?.map((body) => body.id));
											dispatch(updateCommentCountAfterClearingComments(bodyIds[0]));
										}
										break;
									}
									case entities.FormBody: {
										dispatch(updateCommentCountAfterClearingComments([_entityUid]));
										break;
									}
									case entities.Document: {
										let bodyIds = [];
										if (Object.keys(comments) && Object.keys(comments).length > 0) {
											Object.keys(comments).map((key) => {
												if (parseInt(key)) {
													bodyIds.push(parseInt(key));
												}
											});
										}
										if (bodyIds.length > 0) {
											dispatch(updateCommentCountAfterClearingComments(bodyIds));
										}
									}
								}
							}
						})
						.then(() => {
							if (documentWithUnresolvedComments.length > 0) {
								dispatch(getDocumentValidations(engagementId, documentWithUnresolvedComments));
							}
						});
				});
			}
			if (callback) {
				callback();
			}
		});
	};

	const clearAllCommentsOnEmbeddedDocuments = (documentWithUnresolvedComments) => {
		let callArray = documentWithUnresolvedComments.map((doc) => {
			return dispatch(clearComments(engagementId, doc.id, entities.Document, doc.id.toString()));
		});
		return Promise.allSettled(callArray);
	};

	const getDocumentsWithUnresolvedComments = (bodyTypeId, entityDocuments, allValidations) => {
		const documentWithUnresolvedComments = [];
		if (bodyTypeId) {
			const bodyData = bodyTypesWithEmbeddedDocumentTypes.find((body) => body.bodyTypeId === bodyTypeId);
			if (bodyData) {
				const embeddedDocuments = Object.values(entityDocuments.byId).filter(
					(e) => e.documentTypeId === bodyData.documentTypeId
				);
				embeddedDocuments.forEach((document) => {
					const validation = allValidations[document.id];
					if (validation) {
						const hasUnresolvedComment = validation.find(
							(v) => v.validationTypeId === validationTypes.CanvasFromComments && v.validationCount > 0
						);
						if (hasUnresolvedComment) {
							documentWithUnresolvedComments.push(document);
						}
					}
				});
			}
		}
		return documentWithUnresolvedComments;
	};

	useEffect(() => {
		if (clearAllComments === true) {
			execute();
		}
	}, [clearAllComments]);
	return {setClearAllComments, setEntityId, setEntityUid};
}

/**
 * Provides a form values state object, and a callback to update the state.
 * @param {object} initialState The form's initial state.
 * @returns {Array} An array containing the state object, and a useCallback function that updates
 * the consumer component's state accoringly.
 */
export function useForm(initialState = {}) {
	const [state, setState] = useState(initialState);

	const _setState = useCallback((name, value) => {
		// Updates the state, based on the previous value
		setState((prevState) => ({
			...prevState,
			[name]: value
		}));
	}, []);

	return [state, useCallback((name, value) => _setState(name, value), [_setState])];
}

export const useModal = (initialState = {}) => {
	const [show, setShow] = useState(initialState);
	return [show, useCallback((entityId) => setShow((state) => ({...state, [entityId]: !state[entityId]})), [])];
};

export const getShownModalEntityId = (state) =>
	Object.entries(state).find(([_, value]) => value) && Object.entries(state).find(([_, value]) => value)[0];

/**
 * Custom hook to help parsing query string parameters from the url.
 * @returns {Object} Object with query string parameters.
 */
export function useQuery() {
	const {search} = useLocation();

	const searchParams = useMemo(() => new URLSearchParams(search), [search]);

	let queryStringParameters = {};
	for (const [name, value] of searchParams.entries()) {
		if (name.toLowerCase() !== 'referrer') {
			queryStringParameters[name.toLowerCase()] = value;
		}
	}

	return queryStringParameters;
}

export function useQuickLinks() {
	return useSelector((state) => state.quickLink);
}

/**
 * Custom hooks to help switch between document within the same engagement.
 * @param {Object} options object.
 * @returns {Object} Object that contains switchDocument function.
 */
export function useDocumentSwitcher({includeApprovals = false, includeReviewNotes = false, ensureUsers = false}) {
	const dispatch = useDispatch();

	const engagementId = useEngagementId();

	const isAresView = useIsAresView();

	const engagement = useSelector((store) => store.engagement);

	const isMestEngagement = useMemo(() => engagement?.isMultiEntityEngagement, [engagement]);

	const currentEngagment = useMemo(() => {
		if (!engagement) {
			return {engagementId};
		} else if (engagement && !engagement.engagementId) {
			return {...engagement, engagementId};
		} else {
			return engagement;
		}
	}, [engagement, engagementId]);

	const switchDocument = useCallback(
		(documentId) => {
			dispatch(setDocumentId(documentId));

			const promises = [];

			const getDocumentByIdParameters = {
				expand: documentsApiExpandableItems.tasks,
				includeApprovals,
				includeReviewNotes
			};

			getDocumentByIdParameters.includeRelatedEntities = true;

			let firstDocumentStEntityId;

			const getDocumentById = new Promise((resolve) => {
				dispatch(getDocumentByDocumentId(engagementId, documentId, getDocumentByIdParameters, isAresView)).then(
					(document) => {
						if (document.entities?.length > 0) {
							firstDocumentStEntityId = document.entities[0].id;
						}
						return resolve(document);
					}
				);
			});

			promises.push(getDocumentById);

			promises.push(
				dispatch(expandableGetFormById(currentEngagment, documentId, undefined, {resolve: true}, undefined, isAresView))
			);

			const promise = Promise.all(promises)
				.then((responses) => {
					const document = responses[0];

					if (document && document.approvals && document.approvals.length > 0) {
						dispatch(setApprovalsFromDocument(document.approvals, document.id));
					}

					const form = responses[1][0];

					getFormValidations(engagementId, document, undefined, dispatch, form.stEntityId);

					const queryParameters = firstDocumentStEntityId ? {stEntityId: firstDocumentStEntityId} : undefined;

					return dispatch(getMateriality(engagementId, undefined, queryParameters));
				})
				.catch(() => {
					dispatch(raiseJSValidationError(labels.invalidDocumentId));
				});

			if (ensureUsers) {
				dispatch(getActiveAndGuestFormUsers(Number(engagementId), documentId));
			}

			return promise;
		},
		[
			dispatch,
			includeApprovals,
			includeReviewNotes,
			isMestEngagement,
			engagementId,
			isAresView,
			currentEngagment,
			ensureUsers
		]
	);

	return useMemo(() => {
		return {
			switchDocument
		};
	}, [switchDocument]);
}

export function useMenuClick() {
	const [isMenuExpanded, setIsMenuExpanded] = useState(false);

	const isDrawerMode = useIsDrawerMode();

	const frameService = useFrameService();

	const subscriptionRef = useRef(null);

	const clickListener = useCallback(() => {
		frameService.sendMessageToParent({subEvent: frameSubEvent.CLOSE_MENU});
	}, [frameService]);

	useEffect(() => {
		subscriptionRef.current = frameService.subscribeFrame({
			next: (args) => {
				switch (args.event) {
					case frameCommunicationEvents.MESSAGE:
						if (args.subEvent === frameSubEvent.MORE_MENU_CLICK) {
							setIsMenuExpanded(args.isExpanded);
						}

						if (args.subEvent === frameSubEvent.CLOSE_MENU) {
							window.dispatchEvent(new CustomEvent('closeMenu'));
						}
						break;
				}
			}
		});

		return () => {
			if (subscriptionRef.current && !subscriptionRef.current.closed) {
				subscriptionRef.current.unsubscribe();
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (isMenuExpanded && isDrawerMode && window.top !== window.self) {
			window.addEventListener('click', clickListener, false);
		}

		return () => {
			window.removeEventListener('click', clickListener, false);
		};
	}, [clickListener, isDrawerMode, isMenuExpanded]);
}

export function useRefresh() {
	const subscriptionRef = useRef(null);

	const frameService = useFrameService();

	const {headerId, sectionId} = useAresUrlParameters();
	const bodies = useSelector((store) => getFormBodyDataBySectionId(store, sectionId));

	const needsAccountRefresh = useMemo(
		() =>
			bodies &&
			bodies.find(
				(body) => body.bodyTypeId === formBodyType.ALRAAccountList || body.bodyTypeId === formBodyType.ALRASummary
			) !== undefined,
		[bodies]
	);

	const dispatchEvent = useCallback(
		({headerId, sectionId}) => {
			if (needsAccountRefresh) {
				window.dispatchEvent(new CustomEvent(clientSideEventNames.getAccountsForBody));
			} else {
				window.dispatchEvent(
					new CustomEvent(clientSideEventNames.getBodiesBySectionId, {
						detail: {headerId, sectionId}
					})
				);
			}
		},
		[needsAccountRefresh]
	);

	useEffect(() => {
		if (Number.isFinite(headerId) && Number.isFinite(sectionId) && headerId !== 0 && sectionId !== 0) {
			subscriptionRef.current = frameService.subscribeFrame({
				next: (args) => {
					switch (args.event) {
						case frameEvents.DRAWER_TOGGLED:
							if (!args.open) {
								dispatchEvent({headerId, sectionId});
							}
							break;
						case frameEvents.REMOVE:
							if (args.last) {
								dispatchEvent({headerId, sectionId});
							}
							break;
					}
				}
			});
		}

		return () => {
			if (subscriptionRef.current && !subscriptionRef.current.closed) {
				subscriptionRef.current.unsubscribe();
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatchEvent, headerId, sectionId]);
}

export function useActiveFrame() {
	const [toggled, setToggled] = useState(false);

	const [frameKey, setFrameKey] = useState(null);

	const frameService = useFrameService();

	const subscriptionRef = useRef(null);

	useEffect(() => {
		subscriptionRef.current = frameService.subscribeFrame({
			next: (args) => {
				switch (args.event) {
					case frameEvents.ACTIVE_FRAME:
						setToggled(true);
						setFrameKey(args.key);
						break;

					case frameEvents.REMOVE:
					case frameEvents.CLEAR_FRAMES:
						setToggled(false);
						setFrameKey(null);
						break;

					case frameEvents.DRAWER_TOGGLED:
						setToggled(args.open);
						break;
				}
			}
		});

		return () => {
			if (subscriptionRef.current && !subscriptionRef.current.closed) {
				subscriptionRef.current.unsubscribe();
			}
		};
	}, []);

	return {toggled, frameKey};
}

export function useFrameTitle() {
	const frameService = useFrameService();

	const subscriptionRef = useRef(null);

	const updateFrameTitle = useCallback(({name, deepLinkSrc, frameKey}) => {
		if (frameKey) {
			frameService.updateFrame(frameKey, {
				name,
				deepLinkSrc
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		subscriptionRef.current = frameService.subscribeFrame({
			next: (args) => {
				switch (args.event) {
					case frameCommunicationEvents.MESSAGE:
						if (args.subEvent === frameSubEvent.UPDATE_FRAME_TITLE && args.updateFrameTitle) {
							updateFrameTitle(args);
						}
						break;
				}
			}
		});

		return () => {
			if (subscriptionRef.current && !subscriptionRef.current.closed) {
				subscriptionRef.current.unsubscribe();
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [updateFrameTitle]);
}

export function useNav() {
	const internalUser = useSelector((store) => store.internalUser);

	const engagementMetadata = useSelector((store) => store.engagementMetaData);

	const engagement = useSelector((store) => store.engagement);

	const isAresView = useSelector((store) => store.isAresView);

	const user = useUserPreferences();

	const engagementId = useEngagementId();

	const dispatch = useDispatch();

	const isDrawerMode = useIsDrawerMode();

	const isInternalUserWithIndependence = useMemo(() => {
		return internalUser && internalUser.independenceInformation ? true : false;
	}, [internalUser]);

	const getEngagementMetadata = useCallback(() => {
		const engagementId = Utility.getEngagementIdFromUrl(window);

		/**
		 * when multiple datacenters are allowed we will set showdatacenterengagementsonly eq false and
		 * will have to change the url creation above in bindEngagementOptions
		 */

		const engagementStatusIds = [
			EngagementStatus.Active,
			EngagementStatus.Restored,
			EngagementStatus.MarkedForDeletion,
			EngagementStatus.CanvasExternalAccessPortalV2
		];

		const engagementStatusId = `engagementstatusid eq '${engagementStatusIds.join(',')}'`;

		// eslint-disable-next-line max-len
		const query = `filters=engagementid eq ${engagementId},showdatacenterengagementsonly eq true,showaccessibleengagementsonly eq true, ${engagementStatusId}`;

		dispatch(getEngagementMetaData(env.getUniversalUrl(`${ENGAGEMENTMETADATA_URL}?${query}`)));
	}, [dispatch]);

	const getEngagementInformation = useCallback(() => {
		const isEngagementAccessible = internalUser?.independenceInformation?.isEngagementAccessible;

		dispatch(getEngagement(engagementId, isEngagementAccessible)).catch((result) => {
			if (result && result.response && result.response.status === 403) {
				dispatch(raiseJSValidationError(labels.invalidEngagementId));
			}
		});
	}, [dispatch, engagementId, internalUser?.independenceInformation?.isEngagementAccessible]);

	const getFeatureConfigurations = useCallback(() => {
		return dispatch(getConfigurations(env.getBaseURL(serviceURL) + 'FeatureConfigurations'));
	}, [dispatch]);

	const getEngagementQuickLinks = useCallback(() => {
		return dispatch(getQuickLinks(env.getBaseURL(serviceURL, Number(engagementId)) + 'QuickLinks')).catch((result) => {
			if (result && result.response && result.response.status === 403) {
				dispatch(raiseJSValidationError(labels.invalidEngagementId));
			}
		});
	}, [dispatch, engagementId]);

	useEffect(() => {
		if (isInternalUserWithIndependence) {
			if (user && user.id && !user.isGuestUser) {
				if (!engagementMetadata) {
					getEngagementMetadata();
				}
			}
		}
	}, [engagementMetadata, isInternalUserWithIndependence, user, getEngagementMetadata]);

	useEffect(() => {
		if (isInternalUserWithIndependence && isDrawerMode) {
			if (!isAresView && !engagement?.engagementId) {
				getEngagementInformation();
			}
		}
	}, [isDrawerMode, engagement?.engagementId, isInternalUserWithIndependence, isAresView, getEngagementInformation]);

	useEffect(() => {
		if (isInternalUserWithIndependence && isDrawerMode) {
			getFeatureConfigurations().then(() => {
				getEngagementQuickLinks();
			});
		}
	}, [isDrawerMode, isInternalUserWithIndependence, getFeatureConfigurations, getEngagementQuickLinks]);
}

//Use this customHook to track why your component is re-rendering.
export function useTraceUpdate(props, key) {
	const prev = useRef(props);
	useEffect(() => {
		const changedProps = Object.entries(props).reduce((ps, [k, v]) => {
			if (prev.current[k] !== v) {
				ps[k] = [prev.current[k], v];
			}
			return ps;
		}, {});
		if (Object.keys(changedProps).length > 0) {
			console.log(`${key} - changed props:`, changedProps);
		}
		prev.current = props;
	});
}

export function useTaskId(documentId) {
	const currentDocument = useSelector((store) => getTaskDocumentByDocumentId(store, documentId));
	const query = useQuery();

	return query?.taskid ?? currentDocument?.taskId;
}
